import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from '../services/api.service';
import { Role } from '../model/role';

interface ModuleNode {
  id: string;
  label: string;
  sub: string;
  icon: string;
  route?: string;
  x: number;          // percent of diagram width
  y: number;          // percent of diagram height
  kind: 'core' | 'group' | 'leaf';
  access?: string;    // key into accessMap — mirrors sidebar role rules
  parent?: string;    // leaf -> its group (for the connecting line)
  color: string;      // accent colour for the node
  delay: number;      // staggered entrance (ms)
}

interface Link {
  x1: number; y1: number;
  x2: number; y2: number;
  color: string;
  delay: number;
}

/**
 * Post-login landing page. A radial "command center" diagram with SOMWATER at
 * the core and the three suites — Utility Ops, CRM, ERP — orbiting it, each
 * with its modules fanned out on the outer ring. Nodes navigate on click.
 */
@Component({
  selector: 'app-command-center',
  templateUrl: './command-center.component.html',
  styleUrls: ['./command-center.component.scss']
})
export class CommandCenterComponent implements OnInit, OnDestroy {

  now = new Date();
  operator = localStorage.getItem('loggedInUser') || 'OPERATOR';
  private clock: any;

  nodes: ModuleNode[] = [];
  links: Link[] = [];

  // Group accent colours
  private readonly C_OPS = '#00ffc8';   // utility ops — teal/green
  private readonly C_CRM = '#4db8ff';   // crm — blue
  private readonly C_ERP = '#ffb84d';   // erp — amber

  // Access keys mirror the sidebar role checks in mainlayout.component.
  // Everything except 'open' defaults to false (fail closed) until roles load.
  private accessMap: Record<string, boolean> = { open: true };

  constructor(
    private router: Router,
    private apiService: ApiService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.clock = setInterval(() => (this.now = new Date()), 1000);
    this.buildGraph();
    this.loadAccess();
  }

  private loadAccess(): void {
    const loggedInUser = window.localStorage.getItem('loggedInUser');
    if (!loggedInUser) { return; }
    this.apiService.getEmplyeeDetailsById(loggedInUser).subscribe({
      next: (data: any) => {
        const roles = Array.isArray(data?.roles) ? data.roles.map((r: Role) => r.roleName) : [];
        const dept = data?.employeeDepartment;
        const isSys = roles.includes('sysadmin');
        const sysOrFin = isSys || roles.includes('finance');
        this.accessMap = {
          open: true,
          billing: sysOrFin || roles.includes('admin'),
          fieldops: isSys || roles.includes('customercare'),
          crm: sysOrFin || roles.includes('customercare') || roles.includes('customerservice'),
          expenses: sysOrFin || roles.includes('requester') || roles.includes('auditor') || roles.includes('approver'),
          employees: sysOrFin || (dept === 'collector' && (roles.includes('admin') || roles.includes('superadmin'))),
          payroll: isSys || roles.includes('hr'),
          erpadmin: sysOrFin,
        };
      },
      error: () => { /* fail closed — keep defaults */ }
    });
  }

  ngOnDestroy(): void {
    if (this.clock) { clearInterval(this.clock); }
  }

  go(node: ModuleNode): void {
    if (!node.route) { return; }
    if (this.accessMap[node.access || 'open']) {
      this.router.navigate([node.route]);
    } else {
      this.snackBar.open(
        "You don't have access. Contact your administrator for access.",
        'Close',
        { duration: 4000 }
      );
    }
  }

  trackById(_i: number, n: ModuleNode): string { return n.id; }

  private buildGraph(): void {
    const cx = 50, cy = 50;

    // ---- core ----
    this.nodes.push({
      id: 'core', label: 'SOMWATER', sub: 'WATER UTILITY', icon: 'hub',
      x: cx, y: cy, kind: 'core', color: '#8ffcff', delay: 0
    });

    // ---- three suite groups on the inner ring (r=22) ----
    const groups: Array<{ id: string; label: string; sub: string; icon: string; angle: number; color: string }> = [
      { id: 'ops', label: 'UTILITY OPS', sub: 'FIELD & METERING', icon: 'water_drop', angle: 90,  color: this.C_OPS },
      { id: 'crm', label: 'CRM',         sub: 'CUSTOMER CARE',    icon: 'support_agent', angle: 210, color: this.C_CRM },
      { id: 'erp', label: 'ERP',         sub: 'BUSINESS BACKOFFICE', icon: 'account_balance', angle: 330, color: this.C_ERP },
    ];

    const R_GROUP = 22;
    groups.forEach((g, gi) => {
      const rad = (g.angle * Math.PI) / 180;
      const gx = cx + R_GROUP * Math.cos(rad);
      const gy = cy - R_GROUP * Math.sin(rad);
      this.nodes.push({
        id: g.id, label: g.label, sub: g.sub, icon: g.icon,
        x: gx, y: gy, kind: 'group', color: g.color, delay: 200 + gi * 120
      });
      this.links.push({ x1: cx, y1: cy, x2: gx, y2: gy, color: g.color, delay: 200 + gi * 120 });
    });

    // ---- leaf modules on the outer ring (r=44), fanned ±50° around each group ----
    const leaves: Record<string, Array<{ label: string; icon: string; route?: string; access?: string }>> = {
      ops: [
        { label: 'Meters',        icon: 'water',        route: '/meters',            access: 'fieldops' },
        { label: 'Billing',       icon: 'receipt_long', route: '/billing-dashboard', access: 'billing' },
        { label: 'Field Map',     icon: 'map',          route: '/locations',         access: 'fieldops' },
        { label: 'Meter Changes', icon: 'swap_horiz',   route: '/meter-changes',     access: 'billing' },
        { label: 'Bill Reports',  icon: 'assessment',   route: '/billreports',       access: 'billing' },
      ],
      crm: [
        { label: 'Customers',     icon: 'people',       route: '/customers',         access: 'open' },
        { label: 'Complaints',    icon: 'report',       route: '/complaints-dashboard', access: 'crm' },
        { label: 'New Complaint', icon: 'add_comment',  route: '/complaint',         access: 'crm' },
        { label: 'Transfers',     icon: 'sync_alt',     route: '/customer-transfer-report', access: 'crm' },
        { label: 'Audit Log',     icon: 'history',      route: '/audit-log',         access: 'erpadmin' },
      ],
      erp: [
        { label: 'Expenses',      icon: 'payments',     route: '/expenses',          access: 'expenses' },
        { label: 'Employees',     icon: 'badge',        route: '/employees',         access: 'employees' },
        { label: 'Payroll',       icon: 'request_quote', route: '/payroll-runs',     access: 'payroll' },
        { label: 'Inventory',     icon: 'inventory_2',  route: '/inventory-management', access: 'erpadmin' },
        { label: 'Assets',        icon: 'domain',       route: '/asset-management',  access: 'erpadmin' },
        { label: 'Bank',          icon: 'account_balance', route: '/bank',           access: 'erpadmin' },
      ],
    };

    const R_LEAF = 44;
    const SPREAD = 50; // degrees either side of the group angle
    groups.forEach((g, gi) => {
      const items = leaves[g.id] || [];
      const n = items.length;
      const grad = (g.angle * Math.PI) / 180;
      const gx = cx + R_GROUP * Math.cos(grad);
      const gy = cy - R_GROUP * Math.sin(grad);
      items.forEach((item, li) => {
        const offset = n === 1 ? 0 : -SPREAD + (2 * SPREAD * li) / (n - 1);
        const a = ((g.angle + offset) * Math.PI) / 180;
        const lx = cx + R_LEAF * Math.cos(a);
        const ly = cy - R_LEAF * Math.sin(a);
        const delay = 500 + gi * 150 + li * 90;
        this.nodes.push({
          id: `${g.id}-${li}`, label: item.label, sub: '', icon: item.icon,
          route: item.route, access: item.access, x: lx, y: ly, kind: 'leaf',
          parent: g.id, color: g.color, delay
        });
        this.links.push({ x1: gx, y1: gy, x2: lx, y2: ly, color: g.color, delay });
      });
    });
  }
}
